import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  vkBotApi,
  type VkBotSettings,
} from '../lib/vkBotApi';
import { hasPermission, type VkAppUser } from '../lib/authApi';
import { TestReplyModal } from '../components/TestReplyModal';

const PROVIDER_LABELS: Record<string, string> = {
  gigachat: 'GigaChat (Сбер)',
  anthropic: 'Claude (Anthropic)',
  openai: 'ChatGPT (OpenAI)',
};

/** Настройки группы: провайдер/тон/лимиты/знания/зоны + тест. */
export function GroupSettingsPage({ user }: { user: VkAppUser }): JSX.Element {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canManage = hasPermission(user, 'vk_bot:manage');
  const [testOpen, setTestOpen] = useState(false);
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [callbackToken, setCallbackToken] = useState('');
  const [draft, setDraft] = useState<VkBotSettings | null>(null);

  const groupQuery = useQuery({
    queryKey: ['vk-bot', 'group', id],
    queryFn: () => vkBotApi.getGroup(id),
    enabled: Boolean(id),
  });
  const llmQuery = useQuery({
    queryKey: ['vk-bot', 'llm-status'],
    queryFn: () => vkBotApi.llmStatus(),
  });
  // Геокодер и зоны доставки переехали в админку CRM (владелец
  // 2026-08-27) — здесь их больше нет.

  // Локальный черновик настроек — сохраняется кнопкой
  useEffect(() => {
    if (groupQuery.data) setDraft(groupQuery.data.settings);
  }, [groupQuery.data]);

  const saveMut = useMutation({
    mutationFn: (settings: VkBotSettings) => vkBotApi.updateGroup(id, { settings }),
    onSuccess: async () => {
      void message.success('Настройки сохранены');
      await qc.invalidateQueries({ queryKey: ['vk-bot'] });
    },
    onError: (err) =>
      void message.error(err instanceof Error ? err.message : 'Не удалось сохранить'),
  });

  const removeMut = useMutation({
    mutationFn: () => vkBotApi.removeGroup(id),
    onSuccess: async () => {
      void message.success('Группа отключена');
      await qc.invalidateQueries({ queryKey: ['vk-bot'] });
      navigate('/');
    },
  });

  const callbackMut = useMutation({
    mutationFn: () => vkBotApi.refreshCallback(id, callbackToken.trim() || undefined),
    onSuccess: async (res) => {
      if (res.ok) {
        void message.success('События ВК настроены — бот принимает комментарии');
        setCallbackOpen(false);
        setCallbackToken('');
        await qc.invalidateQueries({ queryKey: ['vk-bot'] });
      } else {
        void message.error(`VK отказал: ${res.error ?? 'неизвестная ошибка'}`);
      }
    },
    onError: (err) =>
      void message.error(err instanceof Error ? err.message : 'Не удалось настроить'),
  });

  const group = groupQuery.data;
  if (!group || !draft) {
    return <Card loading style={{ minHeight: 300 }} />;
  }

  const providers = llmQuery.data?.providers ?? [];

  const set = <K extends keyof VkBotSettings>(key: K, value: VkBotSettings[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Space size={12}>
          <Link to="/">
            <Button icon={<ArrowLeftOutlined />}>К группам</Button>
          </Link>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {group.label}
          </Typography.Title>
          {!group.callbackOk && <Tag color="red">callback не настроен</Tag>}
        </Space>
        <Space>
          {!group.callbackOk && (
            <Button danger onClick={() => setCallbackOpen(true)} disabled={!canManage}>
              ⚡ Настроить события ВК
            </Button>
          )}
          <Button
            icon={<ExperimentOutlined />}
            onClick={() => setTestOpen(true)}
            disabled={!canManage}
          >
            Тест бота
          </Button>
          <Button
            type="primary"
            loading={saveMut.isPending}
            disabled={!canManage}
            onClick={() => saveMut.mutate(draft)}
          >
            Сохранить
          </Button>
        </Space>
      </Space>

      <Tabs
        items={[
          {
            key: 'bot',
            label: 'Бот',
            children: (
              <Card>
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <div>
                    <Typography.Text strong>LLM-провайдер</Typography.Text>
                    <div style={{ marginTop: 8 }}>
                      <Radio.Group
                        value={draft.llmProvider}
                        onChange={(e) => set('llmProvider', e.target.value)}
                      >
                        <Space direction="vertical">
                          {(['gigachat', 'anthropic', 'openai'] as const).map((p) => {
                            const st = providers.find((x) => x.provider === p);
                            const enabled = st?.enabled ?? false;
                            return (
                              <Radio key={p} value={p} disabled={!enabled}>
                                {PROVIDER_LABELS[p]}{' '}
                                {enabled ? (
                                  <Tag color="green">доступен</Tag>
                                ) : (
                                  <Tag>{st?.disabledReason ?? 'недоступен'}</Tag>
                                )}
                              </Radio>
                            );
                          })}
                        </Space>
                      </Radio.Group>
                    </div>
                  </div>
                  <div>
                    <Typography.Text strong>Тон и персона</Typography.Text>
                    <Input.TextArea
                      rows={3}
                      value={draft.tone}
                      onChange={(e) => set('tone', e.target.value)}
                      style={{ marginTop: 8 }}
                      placeholder="Дружелюбный менеджер, на «вы», лёгкий юмор…"
                    />
                  </div>
                  <Space size={24} wrap>
                    <div>
                      <Typography.Text strong>Имя бота (опц.)</Typography.Text>
                      <Input
                        value={draft.botName}
                        onChange={(e) => set('botName', e.target.value)}
                        style={{ marginTop: 8, width: 220 }}
                        placeholder="Например: Марина"
                      />
                    </div>
                    <div>
                      <Typography.Text strong>Температура</Typography.Text>
                      <InputNumber
                        min={0}
                        max={2}
                        step={0.1}
                        value={draft.temperature}
                        onChange={(v) => set('temperature', v ?? 0.7)}
                        style={{ marginTop: 8, display: 'block' }}
                      />
                    </div>
                    <div>
                      <Typography.Text strong>Задержка ответа, сек</Typography.Text>
                      <InputNumber
                        min={0}
                        max={600}
                        value={draft.replyDelaySeconds}
                        onChange={(v) => set('replyDelaySeconds', v ?? 0)}
                        style={{ marginTop: 8, display: 'block' }}
                      />
                    </div>
                  </Space>
                </Space>
              </Card>
            ),
          },
          {
            key: 'limits',
            label: 'Лимиты и стоп-слова',
            children: (
              <Card>
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <Space size={24} wrap>
                    <div>
                      <Typography.Text strong>Макс. ответов одному человеку на пост</Typography.Text>
                      <InputNumber
                        min={1}
                        max={20}
                        value={draft.maxRepliesPerUserPerPost}
                        onChange={(v) => set('maxRepliesPerUserPerPost', v ?? 3)}
                        style={{ marginTop: 8, display: 'block' }}
                      />
                    </div>
                    <div>
                      <Typography.Text strong>Макс. ответов на пост (всего)</Typography.Text>
                      <InputNumber
                        min={1}
                        max={200}
                        value={draft.maxRepliesPerPost}
                        onChange={(v) => set('maxRepliesPerPost', v ?? 30)}
                        style={{ marginTop: 8, display: 'block' }}
                      />
                    </div>
                  </Space>
                  <div>
                    <Typography.Text strong>Стоп-слова</Typography.Text>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>
                      Комментарий содержит слово → бот молчит. Enter — добавить.
                    </div>
                    <Select
                      mode="tags"
                      style={{ width: '100%', marginTop: 8 }}
                      value={draft.stopWords}
                      onChange={(v) => set('stopWords', v)}
                      tokenSeparators={[',']}
                      placeholder="суд, прокуратура, юрист…"
                      open={false}
                    />
                  </div>
                </Space>
              </Card>
            ),
          },
          {
            key: 'knowledge',
            label: 'Знания',
            children: (
              <Card>
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <div>
                    <Typography.Text strong>База знаний</Typography.Text>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>
                      Свободным текстом: как заказать, телефоны, акции, самовывоз.
                      Бот отвечает ТОЛЬКО из этих фактов — что не написано, то
                      «уточните в сообщениях группы».
                    </div>
                    <Input.TextArea
                      rows={10}
                      value={draft.knowledgeBase}
                      onChange={(e) => set('knowledgeBase', e.target.value)}
                      style={{ marginTop: 8 }}
                      placeholder={'Заказать: на сайте fishka-sushi.ru или по тел. …\nСамовывоз: скидка 10%…'}
                    />
                  </div>
                  <div>
                    <Typography.Text strong>Розыгрыши и конкурсы</Typography.Text>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>
                      Что сейчас разыгрываем: условия, сроки, дата итогов. Бот
                      может пересказывать ТОЛЬКО это. Подсказки («горячо/холодно»),
                      результаты и «вы выиграли» бот не говорит никогда — это
                      зашито жёстко. Комментарии-участия (адрес, «+», кодовое
                      слово) под конкурсными постами бот молча пропускает.
                    </div>
                    <Input.TextArea
                      rows={4}
                      value={draft.contestNotes}
                      onChange={(e) => set('contestNotes', e.target.value)}
                      style={{ marginTop: 8 }}
                      placeholder={'До 31.07 конкурс «Найди конверт»: прячем конверт в городе, подсказки в постах. Итоги 01.08 в группе.'}
                    />
                  </div>
                  <div>
                    <Typography.Text strong>График работы</Typography.Text>
                    <Input
                      value={draft.workingHours}
                      onChange={(e) => set('workingHours', e.target.value)}
                      style={{ marginTop: 8 }}
                      placeholder="пн-вс 10:00–22:30, кухня до 22:00"
                    />
                  </div>
                </Space>
              </Card>
            ),
          },
          {
            // Вкладка «Зоны доставки» УБРАНА (владелец 2026-08-27):
            // карта зон переехала в админку CRM (Настройки → «Зоны
            // доставки — карта»), где привязана к ГОРОДУ, а не к группе
            // ВК, и партнёр настраивает свои города сам. Бот берёт
            // полигоны оттуда по городу группы (vk-bot-geo.service).
            key: 'zones-moved',
            label: 'Зоны доставки',
            children: (
              <Card>
                <Alert
                  type="info"
                  showIcon
                  message="Зоны доставки настраиваются в CRM"
                  description={
                    <>
                      Карта зон переехала в основную админку: <b>Настройки → «Зоны доставки —
                      карта»</b>. Там она привязана к городу (группа ВК больше не нужна), есть
                      инструкция, и партнёр может настроить свои города сам. Бот этой группы
                      берёт зоны по городу{group?.cityId ? ` «${group.cityId}»` : ''} автоматически.
                    </>
                  }
                />
              </Card>
            ),
          },
          {
            key: 'danger',
            label: 'Отключение',
            children: (
              <Card>
                <Popconfirm
                  title="Отключить бота от группы?"
                  description="Callback-сервер бота будет удалён из группы ВК, лог ответов удалится."
                  okText="Отключить"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => removeMut.mutate()}
                  disabled={!canManage}
                >
                  <Button danger loading={removeMut.isPending} disabled={!canManage}>
                    Отключить группу от бота
                  </Button>
                </Popconfirm>
              </Card>
            ),
          },
        ]}
      />

      <TestReplyModal
        open={testOpen}
        groupId={id}
        providers={providers}
        onClose={() => setTestOpen(false)}
      />

      <Modal
        open={callbackOpen}
        onCancel={() => setCallbackOpen(false)}
        title="⚡ Настроить приём событий ВК"
        okText="Настроить"
        confirmLoading={callbackMut.isPending}
        onOk={() => callbackMut.mutate()}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Что произойдёт"
            description={
              <span style={{ fontSize: 12 }}>
                CRM создаст в группе callback-сервер «Brasol VK Bot» и подпишет
                его на новые комментарии. Чаще всего первая настройка падает,
                если ключ доступа создан <b>без права «управление сообществом»</b>.
                В этом случае создайте в группе НОВЫЙ ключ (Управление → Работа
                с API → Ключи доступа) с правами <b>«управление сообществом»</b>{' '}
                и <b>«стена»</b> и вставьте его ниже.
              </span>
            }
          />
          <Input.Password
            placeholder="Новый ключ vk1.a.… (пусто = попробовать с текущим)"
            value={callbackToken}
            onChange={(e) => setCallbackToken(e.target.value)}
            autoComplete="off"
          />
        </Space>
      </Modal>
    </Space>
  );
}
